import type { ReactNode, SVGProps } from "react";

type IconName =
  | "brain"
  | "calendar"
  | "check-circle"
  | "chevrons"
  | "clock"
  | "code"
  | "database"
  | "document"
  | "omni"
  | "orbit"
  | "instagram"
  | "linkedin"
  | "location"
  | "rocket"
  | "shield"
  | "target"
  | "users"
  | "x-circle"
  | "youtube";

type IconProps = SVGProps<SVGSVGElement> & {
  name: IconName;
};

export function Icon({ name, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      viewBox="0 0 24 24"
      width="24"
      {...props}
    >
      {iconPaths[name]}
    </svg>
  );
}

const iconPaths: Record<IconName, ReactNode> = {
  brain: (
    <>
      <path d="M10 4a3 3 0 0 0-5 2.3A3.5 3.5 0 0 0 4 12c0 1.5 1 2.9 2.4 3.3A3 3 0 0 0 10 19" />
      <path d="M14 4a3 3 0 0 1 5 2.3A3.5 3.5 0 0 1 20 12c0 1.5-1 2.9-2.4 3.3A3 3 0 0 1 14 19" />
      <path d="M10 7v10" />
      <path d="M14 7v10" />
      <path d="M8 10h2" />
      <path d="M14 10h2" />
      <path d="M8 14h2" />
      <path d="M14 14h2" />
    </>
  ),
  calendar: (
    <>
      <rect height="14" rx="2" width="16" x="4" y="6" />
      <path d="M8 3v6" />
      <path d="M16 3v6" />
      <path d="M4 10h16" />
    </>
  ),
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.3 2.3 4.7-5" />
    </>
  ),
  chevrons: (
    <>
      <path d="m9 7-5 5 5 5" />
      <path d="m15 7 5 5-5 5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  code: (
    <>
      <path d="m8 8-4 4 4 4" />
      <path d="m16 8 4 4-4 4" />
    </>
  ),
  document: (
    <>
      <path d="M8 3h6l4 4v14H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
      <path d="M14 3v5h5" />
      <path d="M10 13h6" />
      <path d="M10 17h4" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="5" rx="7" ry="3" />
      <path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
      <path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
    </>
  ),
  omni: (
    <>
      <path d="M12 3 3.5 18.5h17L12 3Z" />
      <circle cx="12" cy="13" r="3.2" />
      <path d="M12 9.8V6.5" />
      <path d="M8.8 15.6 6.6 18" />
      <path d="M15.2 15.6 17.4 18" />
      <path d="M4.7 13h2.1" />
      <path d="M17.2 13h2.1" />
    </>
  ),
  orbit: (
    <>
      <circle cx="12" cy="12" r="2.4" />
      <path d="M4.5 12c1.5-4.8 4-7.2 7.5-7.2s6 2.4 7.5 7.2c-1.5 4.8-4 7.2-7.5 7.2S6 16.8 4.5 12Z" />
      <path d="M7.2 6.2c3.2 1.3 6.4 5.3 9.6 11.6" />
      <path d="M16.8 6.2c-3.2 1.3-6.4 5.3-9.6 11.6" />
      <path d="M19.5 5.5h.01" />
      <path d="M4.5 18.5h.01" />
    </>
  ),
  instagram: (
    <>
      <rect height="16" rx="4" width="16" x="4" y="4" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M17.5 6.5h.01" />
    </>
  ),
  linkedin: (
    <>
      <path d="M7 9v8" />
      <path d="M7 7h.01" />
      <path d="M11 17V9" />
      <path d="M15 17V12a3 3 0 0 0-3-3h-1" />
      <rect height="14" rx="2" width="16" x="4" y="5" />
    </>
  ),
  location: (
    <>
      <path d="M12 20s6-5.3 6-10a6 6 0 1 0-12 0c0 4.7 6 10 6 10Z" />
      <circle cx="12" cy="10" r="2.2" />
    </>
  ),
  rocket: (
    <>
      <path d="M13 4c3.5.4 5.6 2.5 6 6-2.2.4-4.1 1.5-5.7 3.2L10.8 16 8 13.2l2.8-2.5C12.5 9.1 13.6 7.2 14 5" />
      <path d="M8 13.2 5.5 12 4 14.8 6.8 16.5 8 13.2Z" />
      <path d="M10.8 16 12 18.5 9.2 20 7.5 17.2 10.8 16Z" />
      <circle cx="15.5" cy="8.5" r="1.5" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 6 5.5v5.8c0 4 2.5 7.6 6 9.7 3.5-2.1 6-5.7 6-9.7V5.5L12 3Z" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.8" />
      <circle cx="12" cy="12" r="1.4" />
      <path d="M12 3.5V6" />
      <path d="M12 18v2.5" />
      <path d="M3.5 12H6" />
      <path d="M18 12h2.5" />
    </>
  ),
  users: (
    <>
      <path d="M9 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
      <path d="M16.5 11.5a2.5 2.5 0 1 0 0-5" />
      <path d="M4.5 18a4.5 4.5 0 0 1 9 0" />
      <path d="M14 18a3.5 3.5 0 0 1 5 0" />
    </>
  ),
  "x-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m9.5 9.5 5 5" />
      <path d="m14.5 9.5-5 5" />
    </>
  ),
  youtube: (
    <>
      <path d="M20 8.3a2.6 2.6 0 0 0-1.8-1.8C16.5 6 12 6 12 6s-4.5 0-6.2.5A2.6 2.6 0 0 0 4 8.3 27 27 0 0 0 3.5 12a27 27 0 0 0 .5 3.7 2.6 2.6 0 0 0 1.8 1.8C7.5 18 12 18 12 18s4.5 0 6.2-.5a2.6 2.6 0 0 0 1.8-1.8 27 27 0 0 0 .5-3.7 27 27 0 0 0-.5-3.7Z" />
      <path d="m10 9.7 5 2.3-5 2.3V9.7Z" />
    </>
  )
};
